import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Title } from "lola-framework-ui-test";
import { FloatingField } from "../FloatingField";
import { useStore } from "../../store/globalState";
import {
  payoutClaimCashPickupHabits,
  payoutClaimCashPayers,
  payoutClaimCashStates,
  payoutClaimEstimateOptions,
} from "../../gallery/payoutClaimMock";
import {
  catalogNamedPayer,
  dedupeOfferableCashPayers,
  dedupeOfferableCashPayersForState,
  payerCodesEquivalent,
  resolveCashPickupOption,
} from "../../utils/payerCode";
import type { EstimateOption } from "../../interfaces/payout";
import { BackArrowLight, CloseLight } from "../../assets/navIcons";

export interface CashPickupSelection {
  stateCode: string;
  payerId: string;
  payerName: string;
}

export interface CashPickupDetailsSlotProps {
  options?: EstimateOption[];
  value?: { stateCode?: string; payerId?: string } | null;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (result: CashPickupSelection) => void;
  habitsBusy?: boolean;
  habitsEmpty?: boolean;
}

/**
 * Gallery-safe cash pickup details for payout-claim: mock habits list +
 * state/payer selects filtered by offerable pre-quote payers (live parity).
 */
export const CashPickupDetailsSlot: React.FC<CashPickupDetailsSlotProps> = ({
  options = payoutClaimEstimateOptions,
  value,
  onBack,
  onClose,
  onSubmit,
  habitsBusy = false,
  habitsEmpty = false,
}) => {
  const { t } = useTranslation("payout-claim");
  const { theme } = useStore();
  const { bodyCopy, backgroundBtn, textColorBtn } = theme.whiteView;
  const fieldColors = {
    background: theme.whiteView.background,
    title: theme.whiteView.title,
    bodyCopy,
    inactiveColor: theme.whiteView.subtitile,
    errorColor: theme.whiteView.errorColor,
  };

  const offerablePayerCodes = useMemo(
    () => new Set(dedupeOfferableCashPayers(options)),
    [options]
  );

  const savedHabits = useMemo(() => {
    if (habitsEmpty) return [];
    return payoutClaimCashPickupHabits.filter((h) =>
      [...offerablePayerCodes].some((code) =>
        payerCodesEquivalent(code, h.payerId)
      )
    );
  }, [offerablePayerCodes, habitsEmpty]);

  const [stateCode, setStateCode] = useState<string | null>(
    value?.stateCode ?? null
  );
  const [payerId, setPayerId] = useState<string | null>(value?.payerId ?? null);

  const offerablePayerCodesForState = useMemo(
    () => new Set(dedupeOfferableCashPayersForState(options, stateCode)),
    [options, stateCode]
  );

  const stateOptions = useMemo(() => {
    const all = payoutClaimCashStates;
    if (offerablePayerCodes.size === 0) return all;
    return all.filter(
      (s) => dedupeOfferableCashPayersForState(options, s.code).length > 0
    );
  }, [offerablePayerCodes.size, options]);

  useEffect(() => {
    if (!stateCode) return;
    if (stateOptions.length === 0) return;
    const stillOffered = stateOptions.some(
      (s) => s.code.toUpperCase() === stateCode.toUpperCase()
    );
    if (!stillOffered) {
      setStateCode(null);
      setPayerId(null);
    }
  }, [stateOptions, stateCode]);

  const payerList = useMemo(() => {
    if (!stateCode) return [];
    const fromQuote = [...offerablePayerCodesForState];
    if (fromQuote.length > 0) {
      return fromQuote
        .map((code) => {
          const name = catalogNamedPayer(code, payoutClaimCashPayers);
          return name ? { id: code, name } : null;
        })
        .filter((row): row is { id: string; name: string } => row != null)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const source =
      offerablePayerCodes.size === 0
        ? payoutClaimCashPayers
        : payoutClaimCashPayers.filter((p) =>
            [...offerablePayerCodes].some((code) =>
              payerCodesEquivalent(code, p.id)
            )
          );
    return source.filter((p) => p.name?.trim());
  }, [stateCode, offerablePayerCodesForState, offerablePayerCodes]);

  const effectivePayerId = useMemo(() => {
    if (!payerId) return null;
    const exact = payerList.find((p) => p.id === payerId);
    if (exact) return exact.id;
    const aliased = payerList.find((p) =>
      payerCodesEquivalent(p.id, payerId)
    );
    return aliased?.id ?? null;
  }, [payerId, payerList]);

  const valid = Boolean(
    stateCode &&
      effectivePayerId &&
      resolveCashPickupOption(options, effectivePayerId, stateCode)
  );

  const commit = (nextState: string, nextPayer: string, payerName: string) => {
    onSubmit({ stateCode: nextState, payerId: nextPayer, payerName });
  };

  return (
    <section className="payout-details-slot">
      <header className="payout-details-header">
        <button
          type="button"
          className="payout-header-action payout-back-btn"
          aria-label={t("common.back")}
          onClick={onBack}
        >
          <BackArrowLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
        <Title
          title={t("payout.details.CASH_PICKUP.title")}
          align="center"
          color={bodyCopy}
        />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label={t("common.cancel")}
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>

      <form
        className="payout-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid || !stateCode || !effectivePayerId) return;
          const payer = payerList.find((p) => p.id === effectivePayerId);
          if (!payer?.name) return;
          commit(stateCode, effectivePayerId, payer.name);
        }}
      >
        { (habitsBusy ||
          savedHabits.some(
            (h) =>
              catalogNamedPayer(h.payerId, payoutClaimCashPayers) && h.stateName
          )) && (
          <section
            className="payout-saved"
            aria-label={t("payout.savedDestinations.aria")}
            aria-busy={habitsBusy}
          >
            <span className="payout-field-label">
              {t("payout.savedDestinations.label")}
            </span>
            <div role="list" className="payout-saved-list">
              {habitsBusy ? (
                <>
                  {Array.from({ length: 2 }, (_, i) => (
                    <div
                      key={i}
                      className="payout-saved-skeleton"
                      aria-hidden="true"
                    >
                      <span className="payout-saved-skeleton-line" />
                      <span className="payout-saved-skeleton-line short" />
                    </div>
                  ))}
                </>
              ) : (
                savedHabits.map((habit) => {
                  const title = catalogNamedPayer(
                    habit.payerId,
                    payoutClaimCashPayers
                  );
                  if (!title || !habit.stateName) return null;
                  return (
                    <button
                      key={`${habit.stateCode}:${habit.payerId}`}
                      type="button"
                      role="listitem"
                      className="payout-saved-item"
                      onClick={() =>
                        commit(habit.stateCode, habit.payerId, title)
                      }
                    >
                      <span className="payout-saved-title">{title}</span>
                      <span className="payout-hint">{habit.stateName}</span>
                    </button>
                  );
                })
              )}
            </div>
            <span className="payout-hint payout-or-new">
              {t("payout.savedDestinations.orNew")}
            </span>
          </section>
        )}

        <div className="payout-field">
          <FloatingField
            label={t("payout.fields.state")}
            value={stateCode ?? ""}
            colors={fieldColors}
          >
            <select
              style={{ color: bodyCopy, cursor: "pointer" }}
              value={stateCode ?? ""}
              onChange={(e) => {
                setStateCode(e.target.value || null);
                setPayerId(null);
              }}
            >
              <option value="" />
              {stateOptions.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
          </FloatingField>
          {stateOptions.length === 0 && (
            <span className="payout-hint">{t("payout.fields.noStates")}</span>
          )}
        </div>

        {stateCode && (
          <div className="payout-field">
            <FloatingField
              label={t("payout.fields.payer")}
              value={effectivePayerId ?? ""}
              colors={fieldColors}
            >
              <select
                style={{ color: bodyCopy, cursor: "pointer" }}
                value={effectivePayerId ?? ""}
                onChange={(e) => setPayerId(e.target.value || null)}
              >
                <option value="" />
                {payerList.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </FloatingField>
            {payerList.length === 0 && (
              <span className="payout-hint">{t("payout.fields.noPayers")}</span>
            )}
          </div>
        )}

        <Button
          type="submit"
          size="large"
          disabled={!valid}
          background={backgroundBtn}
          color={textColorBtn}
        >
          {t("common.continue")}
        </Button>
      </form>
    </section>
  );
};
